/**
 * UTM Parameter Tracking
 *
 * Captures standard UTM parameters from marketing URLs, stores them
 * in localStorage, and provides them for PostHog attribution tracking.
 *
 * Usage:
 *   captureUTMParams()     — call on page load / route change
 *   getStoredUTMParams()   — retrieve stored params for analytics
 *   clearUTMParams()       — clear on logout (optional)
 */

const STORAGE_KEY = 'sv-utm-params';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  /** Marketing channel ref (e.g., ?ref=card from QR codes, ?ref=flyer) */
  ref?: string;
}

/**
 * Parse UTM parameters from the current URL and store in localStorage.
 * Only writes if at least one UTM param is present (won't overwrite
 * existing params with an empty object from a non-UTM URL).
 */
export function captureUTMParams(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: UTMParams = {};
    let hasAny = false;

    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) {
        utm[key] = value;
        hasAny = true;
      }
    }

    // Capture marketing ref param (e.g., ?ref=card from QR codes)
    const ref = params.get('ref');
    if (ref) {
      utm.ref = ref;
      hasAny = true;
    }

    if (hasAny) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
    }
  } catch {
    // localStorage may be unavailable in incognito / iframe
  }
}

/**
 * Retrieve stored UTM parameters from localStorage.
 * Returns an empty object if nothing is stored.
 */
export function getStoredUTMParams(): UTMParams {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as UTMParams;
    }
  } catch {
    // parse error or localStorage unavailable
  }
  return {};
}

/**
 * Clear stored UTM parameters (e.g. on logout).
 */
export function clearUTMParams(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
